<template>
  <div>
    <!-- JSON-LD ItemList Schema per catalogo prodotti -->
    <script type="application/ld+json" v-if="itemListSchema">
      {{ JSON.stringify(itemListSchema) }}
    </script>
    <!-- Category Info Header Section (Top) -->
    <section v-if="category" class="category-header">
      <div class="container text-center mb-4">
        <h1 class="page-title">{{ category.headingTitle || category.name }}</h1>
        <p class="page-subtitle">{{ category.description }}</p>
      </div>
    </section>

    <!-- Products Section -->
    <section class="products-section">
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ t('product.status.loading') }}</span>
        </div>
      </div>
      <div v-else-if="error">
        <div class="container">
          <ErrorState 
            :message="error || t('product.error.load_failed')" 
            @retry="retryLoadProducts"
          />
        </div>
      </div>
      <div v-else-if="products.length > 0" class="product-grid-container">
        <header class="section-header">
          <h2 class="section-label">DROP: {{ category?.name?.toUpperCase() }}</h2>
          <span class="product-count">{{ t('product.count_available', { active: activeProductsCount, total: totalProductsCount }) }}</span>
        </header>
        <div
          v-for="product in products"
          :key="product.id"
          class="product-card-wrapper"
        >
          <ProductCard
            :product="product"
            @view-details="openModal"
          />
        </div>
      </div>
      <div v-else class="no-products">
        <p>{{ t('product.status.no_products') }}</p>
      </div>
    </section>

    <!-- SEO Content Section (Bottom) -->
    <section v-if="category && category.seoText" class="seo-section">
      <div class="container">
        <div class="seo-content" v-html="category.seoText"></div>
      </div>
    </section>

    <!-- Modal Container -->
    <ProductModalHorizontal
      v-if="selectedProduct"
      :product="selectedProduct"
      @close="selectedProduct = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCategoryStore } from '../stores/categoryStore'
import { useCatalog } from '../composables/useCatalog'
import ProductCard from '../components/ProductCard.vue'
import ProductModalHorizontal from '../components/ProductModalHorizontal.vue'
import ErrorState from '../components/ErrorState.vue'

import { useLanguage } from '../composables/useLanguage'
const { currentLanguage, t } = useLanguage()

const route = useRoute()
const categoryStore = useCategoryStore()
const { products, isLoading, error, fetchProducts } = useCatalog()
const activeProductsCount = computed(() => {
  return products.value.filter(p => p.isActive !== false).length
})

// JSON-LD ItemList Schema per catalogo prodotti
const itemListSchema = computed(() => {
  if (!category.value || products.value.length === 0) return null
  
  const itemListElements = products.value
    .filter(p => p.isActive !== false)
    .slice(0, 20) // Max 20 items per schema.org recommendation
    .map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "url": `https://billyb.it/t-shirt-streetwear-uomo`,
        "image": product.images && product.images.length > 0 
          ? product.images.map(img => {
              const url = img.imageUrl || img.imageName || img.url
              return url.startsWith('http') ? url : `https://billyb.it${url}`
            }).filter(Boolean)
          : [`https://billyb.it/images/products/luminous-blessed-black-front.webp`],
        "brand": {
          "@type": "Brand",
          "name": "Billy B"
        },
        "offers": {
          "@type": "Offer",
          "url": `https://billyb.it/t-shirt-streetwear-uomo`,
          "priceCurrency": "EUR",
          "price": (product.price || 0).toFixed(2),
          "availability": product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "itemCondition": "https://schema.org/NewCondition",
          "sku": product.categoryTypeSku || String(product.id)
        }
      }
    }))
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": category.value.name,
    "description": category.value.description || `Collezione ${category.value.name} di Billy B`,
    "numberOfItems": products.value.filter(p => p.isActive !== false).length,
    "itemListElement": itemListElements
  }
})
const totalProductsCount = computed(() => products.value.length)

const category = computed(() => {
  // 1. Prova per slug dinamico (se presente nella rotta)
  if (route.params.categorySlug) {
    const foundBySlug = categoryStore.getCategoryBySlug(route.params.categorySlug)
    if (foundBySlug) return foundBySlug
  }
  
  // 2. Prova per slug statico derivato dal path
  const staticSlug = route.path.replace(/^\//, '') || 't-shirt'
  const foundByPath = categoryStore.getCategoryBySlug(staticSlug)
  if (foundByPath) return foundByPath

  // 3. Fallback di emergenza per slug specifico
  const foundByHardcoded = categoryStore.getCategoryBySlug('t-shirt-streetwear-uomo')
  if (foundByHardcoded) return foundByHardcoded

  // 4. Fallback per ID: se avevamo una categoria selezionata, la ritroviamo nell'elenco aggiornato
  // Questo risolve il problema del cambio lingua se lo slug cambia tra IT ed EN
  if (categoryStore.currentCategory) {
    return categoryStore.categories.find(c => c.categoryId === categoryStore.currentCategory.categoryId)
  }
  
  return null
})

const selectedProduct = ref(null)
const isMobile = ref(false)

const loadCategoryData = async () => {
  if (category.value) {
    categoryStore.setCurrentCategory(category.value)
    await fetchProducts({ categoryId: category.value.categoryId })
  } else if (!categoryStore.isLoading) {
    // Se le categorie non sono ancora caricate, aspettiamo lo store
    // (MainLayout si occupa di caricarle all'avvio)
  }
}

// Monitoriamo sia lo slug della rotta, lo store delle categorie e il cambio lingua
watch([() => route.params.categorySlug, () => categoryStore.categories, currentLanguage], async () => {
  await loadCategoryData()
}, { immediate: true })

const openModal = (product) => {
  if (product && product.isActive) {
    selectedProduct.value = product
  }
}

const retryLoadProducts = async () => {
  await fetchProducts({ categoryId: 1 })
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
.products-section {
  padding: 60px 0 40px 0;
  background-color: #fff; /* Cambio in bianco per uniformità con Home */
}

/* Struttura Fluida Edge-to-Edge per la Griglia Prodotti */
.product-grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 2.5rem;
  row-gap: 0.5rem;
  padding: 0 6vw;
  width: 100%;
}

.section-header {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 15px;
  margin-bottom: 1.5rem; /* Margine impostato a 1.5rem come richiesto */
}

.section-label {
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #000;
  margin-bottom: 0;
  font-family: 'Montserrat', sans-serif;
}

.product-count {
  font-size: 14px;
  font-weight: 400;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
}

@media (max-width: 991px) {
  .product-grid-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    padding: 0 4vw;
  }
}

@media (max-width: 576px) {
  .product-grid-container {
    grid-template-columns: 1fr;
    padding: 0 20px;
  }
}

/* Micro-correzioni Info Prodotto per Bilanciamento Lusso/Leggibilità */
.product-card-wrapper :deep(.item-name) {
  font-size: 16px !important;
  font-weight: 700 !important;
}

.product-card-wrapper :deep(.item-price) {
  font-size: 16px !important;
  font-weight: 700 !important;
}

.product-card-wrapper :deep(.item-edition) {
  font-size: 10px !important;
}

/* Effetto Interattivo Premium sulle Immagini dei Capi */
.product-card-wrapper :deep(.image-area) {
  overflow: hidden;
  position: relative;
}

.product-card-wrapper :deep(.block-image) {
  transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.4s ease !important;
}

.product-card-wrapper:hover :deep(.block-image) {
  transform: scale(1.03); /* Zoom fluido impercettibile e di lusso */
}

.page-header {
  background-color: #fff;
  border-bottom: 1px solid #eee;
}

.page-title {
  font-family: 'Inter', sans-serif;
  font-size: 1.6rem;
  font-weight: 800;
  color: #000;
  text-transform: uppercase;
  margin-bottom: 20px;
  letter-spacing: 3px;
}

.category-header {
  background-color: #f8f8f8;
  padding: 40px 0 30px;
  border-bottom: 1px solid #eeeeee;
}

.page-subtitle {
  font-family: 'Inter', sans-serif;
  max-width: 900px;
  margin: 24px auto 0;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.75;
  color: #2d2d2d;
  letter-spacing: 0.2px;
  text-align: center;
  text-wrap: balance;
}

.seo-section {
  background-color: #f8f8f8;
  border-top: 1px solid #2d5559;
  padding-top: 50px;
  padding-bottom: 60px;
}

.seo-content {
  font-family: 'Inter', sans-serif;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.8;
  color: #2d2d2d;
  letter-spacing: 0.2px;
  text-wrap: balance;
}

@media (max-width: 991px) {
  .page-title {
    font-size: 1.75rem;
  }

  .page-subtitle {
    max-width: 900px;
    font-size: 15px;
    padding: 0 20px;
  }

  .seo-content {
    max-width: 900px;
    font-size: 14px;
  }
}

@media (max-width: 576px) {
  .page-title {
    font-size: 1.4rem;
    letter-spacing: 2px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .seo-content {
    font-size: 13px;
    padding: 0 16px;
  }
}

.row {
  row-gap: 40px;
}

.no-products {
  text-align: center;
  padding: 60px 20px;
  color: #2d5559;
  font-size: 18px;
  font-weight: bold;
}

@media (max-width: 768px) {
  .page-header {
    padding: 40px 20px;
  }
}
</style>
